import { useState } from 'react';
import { planRecipients, wantsEmail, wantsSms } from '@/lib/recipientPlan';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

/**
 * SendDirectionsDialog — ad-hoc send of a facility's directions (address +
 * arrival instructions + map link) to one or more emails / phone numbers.
 *
 * When opened from an appointment that has drivers on file, their phones are
 * pre-filled — this IS the "manual text driver" action from the SMS batch
 * spec, reusing this dialog rather than a separate one. An appointment can
 * have several drivers (multi-truck pickup, or an LTL appointment booked
 * with a longer window — see migration 134), so ALL of their phones get
 * pre-filled, not just one. When opened without driver info (e.g. from the
 * Directions Settings tab, or an appointment with no drivers yet), recipients
 * start empty as before — there's nothing to pre-fill.
 *
 * Facility is passed in (from the Directions Settings tab, or from a calendar
 * appointment's facilityCode). Calls the live POST
 * /api/mgmt/facilities/{code}/send-directions endpoint. Both email and SMS
 * are live (toll-free number verified).
 */

interface SendDirectionsDialogProps {
    open: boolean;
    onClose: () => void;
    facilityCode: string;
    facilityLabel?: string;           // optional nicer name for the header
    getToken: () => Promise<string>;
    /** Pre-fill when opened for a specific appointment's driver(s). Omit (or
     *  pass an empty array) when there are no known drivers — recipients
     *  then start empty, same as before. Email isn't snapshotted onto
     *  dock.AppointmentDriver (only name+phone), so only phone pre-fills. */
    drivers?: { name: string; phone: string }[];
    /**
     * The appointment these directions are for, when opened from one.
     *
     * ⚠️ ONLY THE ID TRAVELS. The server looks up the number, window and PO and
     * writes them into the message — the browser does not get to author text
     * that lands on a driver's phone. Omitted when opened from Settings →
     * Directions, where there is no appointment and the message stays
     * facility-only.
     */
    appointmentId?: number;
}

interface SendResponse {
    sent: number;
    anyFailed: boolean;
    note?: string;
    results: { channel: string; recipient: string; ok: boolean; detail?: string; reason?: 'opted_out' }[];
}

export function SendDirectionsDialog({
    open, onClose, facilityCode, facilityLabel, getToken,
    drivers = [], appointmentId,
}: SendDirectionsDialogProps) {
    const hasDrivers = drivers.length > 0;
    const driverPhones = drivers.map(d => d.phone);
    // Default channel: if we know any driver's phone, lead with SMS (that's
    // how a driver actually gets reached); otherwise default to email as before.
    const [channel, setChannel]       = useState<'email' | 'sms' | 'both'>(hasDrivers ? 'sms' : 'email');
    const [emailInput, setEmailInput] = useState('');
    const [phoneInput, setPhoneInput] = useState('');
    const [emails, setEmails]         = useState<string[]>([]);
    const [phones, setPhones]         = useState<string[]>(driverPhones);
    const [sending, setSending]       = useState(false);
    const [msg, setMsg]               = useState<{ kind: 'ok' | 'warn' | 'err'; text: string } | null>(null);

    const reset = () => {
        setChannel(hasDrivers ? 'sms' : 'email'); setEmailInput(''); setPhoneInput('');
        setEmails([]); setPhones(driverPhones);
        setMsg(null); setSending(false);
    };
    const close = () => { reset(); onClose(); };

    const addEmail = () => {
        const v = emailInput.trim();
        if (v && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) && !emails.includes(v)) {
            setEmails([...emails, v]); setEmailInput('');
        }
    };
    const addPhone = () => {
        const v = phoneInput.trim();
        if (v && !phones.includes(v)) { setPhones([...phones, v]); setPhoneInput(''); }
    };

    /**
     * ⛔ THE RULES NOW LIVE IN `@/lib/recipientPlan` (extracted 2026-08-27 for
     * #324). They were all learned here, from real sends that went wrong — and
     * the delivery pin needs every one of them. ⚠️ A SECOND COPY WOULD MEAN ONE
     * DIALOG GETS THE NEXT FIX; read the module for what each rule cost.
     */
    const handleSend = async () => {
        setMsg(null);
        const wantEmail = wantsEmail(channel);
        const wantSms   = wantsSms(channel);

        const plan = planRecipients({ channel, emails, phones, emailInput, phoneInput });
        // ⚠️ COMMIT BEFORE REPORTING — the user must SEE their typed value was
        // accepted. `commit` is false only for an unparseable address, where
        // clearing the box would delete the text they need to correct.
        if (plan.commit) {
            setEmails(plan.emails); setPhones(plan.phones);
            if (wantEmail) setEmailInput('');
            if (wantSms) setPhoneInput('');
        }
        if (!plan.ok) { setMsg({ kind: 'err', text: plan.error ?? 'Add at least one recipient.' }); return; }
        const sendEmails = plan.emails;
        const sendPhones = plan.phones;
        setSending(true);
        try {
            const token = await getToken();
            const res = await fetch(`/api/mgmt/facilities/${facilityCode}/send-directions`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
                body: JSON.stringify(
                    // Send the id only when we have one; the endpoint treats it
                    // as optional and a facility-only send must stay that way.
                    appointmentId
                        ? { channel, emails: sendEmails, phones: sendPhones, appointmentId }
                        : { channel, emails: sendEmails, phones: sendPhones },
                ),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => ({ error: res.statusText }));
                throw new Error(e.error ?? `HTTP ${res.status}`);
            }
            const data = await res.json() as SendResponse;
            // AN OPT-OUT IS NOT A FAILURE. Split on the machine-readable reason,
            // never on the message text. Before 2026-08-16 both landed in one
            // "Failed:" list, which told a scheduler to go chase a delivery
            // problem for someone who had explicitly asked us to stop texting.
            const optedOut = data.results.filter(r => r.reason === 'opted_out').map(r => r.recipient);
            const failed = data.results.filter(r => !r.ok && r.reason !== 'opted_out');
            const failedList = failed.map(r => r.recipient);
            const parts: string[] = [];
            if (data.sent > 0) parts.push(`Sent to ${data.sent} recipient(s).`);
            if (optedOut.length) parts.push(`${optedOut.join(', ')} opted out of texts — send by email instead.`);
            // ⚠️ SHOW WHY IT FAILED (2026-08-17). The engine already returns a
            // usable sentence — "Not a valid phone number: 'lkjlkjsfd'. Use 10
            // digits (US) or +<country code><number>." — and this dialog was
            // printing "Failed: lkjlkjsfd" and dropping it. A scheduler cannot
            // fix a typo they are not told about. `detail` is per recipient, so
            // show it per recipient rather than one blanket message.
            if (failed.length) {
                parts.push(...failed.map(r => r.detail ? `${r.recipient}: ${r.detail}` : `Failed: ${r.recipient}.`));
            }
            if (data.note) {
                setMsg({ kind: 'warn', text: `${data.sent} email(s) sent. ${data.note}` });
            } else if (failedList.length) {
                setMsg({ kind: 'warn', text: parts.join(' ') });
            } else if (optedOut.length) {
                // Nothing to retry — say what happened and leave the recipients in
                // place so switching to email is one click, not a re-entry.
                setMsg({ kind: 'warn', text: parts.join(' ') });
            } else {
                setMsg({ kind: 'ok', text: `Sent to ${data.sent} recipient(s).` });
                // Clear recipients on full success so the dialog is ready for another send.
                setEmails([]); setPhones([]);
            }
        } catch (e) {
            setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'Send failed.' });
        } finally { setSending(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && close()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Send directions</DialogTitle>
                    <DialogDescription>
                        {facilityLabel ? `${facilityLabel} (${facilityCode})` : `Facility ${facilityCode}`} — address, arrival instructions, and a map link.
                        {hasDrivers && (
                            <span className="block mt-1 text-xs font-medium text-foreground">
                                {drivers.length === 1
                                    ? `Texting driver: ${drivers[0].name}`
                                    : `Texting ${drivers.length} drivers: ${drivers.map(d => d.name).join(', ')}`}
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                        {(['email','sms','both'] as const).map(c => (
                            <button key={c} type="button" onClick={() => setChannel(c)}
                                className={`px-3 py-1 rounded-md border text-xs ${channel===c ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                                {c==='email' ? 'Email' : c==='sms' ? 'Text' : 'Email + Text'}
                            </button>
                        ))}
                    </div>

                    {(channel==='email' || channel==='both') && (
                        <div>
                            <div className="flex gap-2">
                                <input type="email" value={emailInput} onChange={e=>setEmailInput(e.target.value)}
                                    onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); addEmail(); } }}
                                    placeholder="driver@example.com"
                                    className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                <button type="button" onClick={addEmail} className="px-3 py-1.5 rounded-md border border-gray-300 text-sm hover:bg-gray-50">Add</button>
                            </div>
                            {emails.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {emails.map(em => (
                                        <span key={em} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs rounded px-2 py-0.5">
                                            {em}<button type="button" onClick={()=>setEmails(emails.filter(x=>x!==em))} className="text-blue-400 hover:text-blue-700">×</button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {(channel==='sms' || channel==='both') && (
                        <div>
                            <div className="flex gap-2">
                                <input type="tel" value={phoneInput} onChange={e=>setPhoneInput(e.target.value)}
                                    onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); addPhone(); } }}
                                    placeholder="+1 555 555 5555"
                                    className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                <button type="button" onClick={addPhone} className="px-3 py-1.5 rounded-md border border-gray-300 text-sm hover:bg-gray-50">Add</button>
                            </div>
                            {phones.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {phones.map(ph => (
                                        <span key={ph} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs rounded px-2 py-0.5">
                                            {ph}<button type="button" onClick={()=>setPhones(phones.filter(x=>x!==ph))} className="text-gray-400 hover:text-gray-700">×</button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {msg && (
                        <div className={`text-xs rounded-md px-3 py-2 ${msg.kind==='ok' ? 'bg-green-50 text-green-700' : msg.kind==='warn' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>{msg.text}</div>
                    )}

                    <div className="flex justify-end gap-2 pt-1">
                        <Button variant="ghost" size="sm" onClick={close} disabled={sending}>Close</Button>
                        <Button variant="default" size="sm" onClick={handleSend} disabled={sending}>
                            {sending ? 'Sending…' : 'Send directions'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

